// Обёртка над shadcn Input/Label + react-hook-form. Source: stage-4-screens.md §4.
//
// Использование (через register, без Controller — input native):
//   <FormField label="Email" type="email" error={errors.email?.message}
//              {...register('email')} />
//
// При наличии error: красная рамка, message ниже. Все нативные атрибуты
// (autoComplete, inputMode, placeholder, type) пробрасываются на <input>.

import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

type FormFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  rightAddon?: ReactNode;
};

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField(
  { label, error, hint, rightAddon, className, ...rest },
  ref,
) {
  const reactId = useId();
  const id = `ff-${reactId}`;
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined;

  return (
    <div className="space-y-1.5">
      {label && (
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
      )}
      <div className="relative">
        <Input
          id={id}
          ref={ref}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          className={cn(
            'h-10',
            error && 'border-destructive focus-visible:ring-destructive',
            rightAddon && 'pr-10',
            className,
          )}
          {...rest}
        />
        {rightAddon && (
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center">
            {rightAddon}
          </div>
        )}
      </div>
      {error && (
        <p id={`${id}-err`} className="text-xs text-destructive">
          {error}
        </p>
      )}
      {!error && hint && (
        <p id={`${id}-hint`} className="text-hl-small text-muted-foreground">
          {hint}
        </p>
      )}
    </div>
  );
});
